/**
 * Shipment Card - Reusable card for displaying shipment summary
 */

import { memo, useState, useEffect } from 'react'
import { useTranslation } from '@ufc/core'
import { Badge, cn, Button, Label, UserCheck, X, User as UserIcon, toast } from '@ufc/uikit'
import {
  Package,
  CheckCircle,
  Clock,
  XCircle,
} from '@ufc/uikit'
import type { Shipment, ShipmentStatus } from '../../domain/types'
import { shipmentsApi } from '../../infrastructure/api/shipments-api'
import { ResourceManagementPanel } from '@ufc/domains/assets'
import type { VehicleType } from '@ufc/domains/assets'

interface ShipmentCardProps {
  shipment: Shipment
  onClick?: () => void
  className?: string
  /** Optional: Vehicle type requirement (mixer/flatbed) for resource selection */
  requiredVehicleType?: VehicleType
  /** Called after resource is successfully assigned/changed on this shipment */
  onResourceUpdated?: (driverName: string | undefined, vehicleLicensePlate: string | undefined) => void
  /** Tenant documentId to scope resource lookups to this tenant only */
  tenantDocumentId?: string | null
  /** When true, hides the resource assignment/change button (e.g. for read-only roles) */
  readOnly?: boolean
}

const statusConfig: Record<ShipmentStatus, { bg: string; text: string; icon: typeof Clock }> = {
  scheduled: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', icon: Clock },
  loading: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400', icon: Package },
  delivered: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400', icon: CheckCircle },
  cancelled: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', icon: XCircle },
}

function ShipmentCardComponent({ shipment: initialShipment, onClick, className, requiredVehicleType, onResourceUpdated, tenantDocumentId, readOnly = false }: ShipmentCardProps) {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [shipment, setShipment] = useState(initialShipment)
  const config = statusConfig[shipment.status]
  const StatusIcon = config.icon

  // Resource Management State
  const [isEditingResource, setIsEditingResource] = useState(false)
  

  // Update local state if prop changes
  useEffect(() => {
    setShipment(initialShipment)
  }, [initialShipment])

  const handleUpdateResource = async (resource: any) => {
    try {
      const resId = resource.documentId || resource.id
      console.log('Assigning resource to shipment:', resId)
      
      const updated = await shipmentsApi.updateShipment(shipment.id, {
        resource: resId
      })

      // Fetch the updated shipment to ensure we have the full relations populated
      const fullShipment = await shipmentsApi.getShipment(shipment.id)
      
      if (fullShipment) {
        setShipment(fullShipment)
        onResourceUpdated?.(fullShipment.driverName ?? undefined, fullShipment.truckNumber ?? undefined)
      } else {
        // Fallback
        if (!updated.driverName && resource.driver?.name) {
          updated.driverName = resource.driver.name
        }
        if (!updated.truckNumber && (resource.powerUnit?.licensePlate || resource.vehicle?.licensePlate || resource.powerUnit?.assetNumber)) {
          updated.truckNumber = resource.powerUnit?.licensePlate || resource.vehicle?.licensePlate || resource.powerUnit?.assetNumber
        }
        setShipment(updated)
        onResourceUpdated?.(updated.driverName ?? undefined, updated.truckNumber ?? undefined)
      }

      setIsEditingResource(false)
      
      toast({ title: t('common.success'), description: t('shipments.resourceUpdated') })
    } catch (error: any) {
      console.error('Failed to update shipment resource:', error)
      const message = error.response?.data?.error?.message || error.message || t('shipments.resourceUpdateFailed')
      toast({ title: t('common.error'), description: message, variant: 'destructive' })
    }
  }

  // Derived resource display
  const resourceNameDisplay = shipment.driverName 
    ? `${shipment.driverName}${shipment.truckNumber ? ` • ${shipment.truckNumber}` : ''}`
    : null

  return (
    <div
      className={cn(
        "p-4 rounded-xl border border-border/50 bg-white dark:bg-slate-800/50 transition-[box-shadow,border-color]",
        onClick && !isEditingResource && "hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-primary/30 hover:shadow-md",
        className
      )}
      onClick={(e) => {
        if (isEditingResource) return
        onClick?.()
      }}
      role={onClick && !isEditingResource ? "button" : undefined}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-foreground truncate">{shipment.shipmentNumber}</p>
          <p className="text-xs text-muted-foreground truncate">
            {isArabic ? shipment.product.ar : shipment.product.en}
          </p>
        </div>
        <Badge className={cn(config.bg, config.text, "border-0 gap-1 shrink-0 ms-2")}>
          <StatusIcon className="w-3 h-3" />
          {t(`shipments.${shipment.status}`)}
        </Badge>
      </div>



      {/* Resource Management Section */}
      <div className="border-t border-border/30 pt-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
           <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t('assets.resourceManagement')}</Label>
           {!isEditingResource && shipment.status === 'scheduled' && !readOnly && (
             <Button
               size="sm"
               variant="ghost"
               className="h-6 px-2 text-[10px] text-primary hover:bg-primary/10"
               onClick={() => setIsEditingResource(true)}
             >
               {resourceNameDisplay ? t('shipments.change') : t('shipments.assign')}
             </Button>
           )}
        </div>

        {!isEditingResource ? (
          <div className="flex items-center gap-3 text-sm">
            {resourceNameDisplay ? (
              <>
                <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center text-emerald-600 border border-emerald-100 dark:border-emerald-900/50">
                  <UserCheck className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                   <p className="font-semibold text-foreground truncate">{shipment.driverName}</p>
                   {shipment.truckNumber && <p className="text-xs text-muted-foreground truncate font-mono">{shipment.truckNumber}</p>}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground italic text-xs py-1">
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <UserIcon className="w-3.5 h-3.5 opacity-50" />
                </div>
                <span>{t('shipments.noResourceAssigned', 'No resource assigned')}</span>
              </div>
            )}
            
            {shipment.eta && (
                <div className="ms-auto flex items-center gap-1.5 text-primary font-bold text-[10px] bg-primary/5 px-2 py-1 rounded-full border border-primary/10">
                    <Clock className="w-3 h-3" />
                    <span>{t('shipments.eta', 'ETA')}: {shipment.eta}</span>
                </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 bg-slate-50/50 dark:bg-slate-800/30 p-3 rounded-xl border border-primary/20 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-primary uppercase tracking-wide">
                {t('shipments.selectResource', 'Select Resource')}
              </span>
              <button
                onClick={() => setIsEditingResource(false)}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-3 h-3 text-muted-foreground" />
              </button>
            </div>
            <ResourceManagementPanel
              requiredVehicleType={requiredVehicleType}
              onSelectResource={handleUpdateResource}
              pageSize={10}
              searchPlaceholder={t('assets.searchResources', 'Search resources...')}
              tenantDocumentId={tenantDocumentId}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export const ShipmentCard = memo(ShipmentCardComponent, (prev, next) => {
  return (
    prev.shipment.id === next.shipment.id &&
    prev.shipment.status === next.shipment.status &&
    prev.shipment.shipmentNumber === next.shipment.shipmentNumber &&
    prev.shipment.driverName === next.shipment.driverName &&
    prev.shipment.truckNumber === next.shipment.truckNumber &&
    prev.shipment.eta === next.shipment.eta &&
    prev.className === next.className &&
    prev.requiredVehicleType === next.requiredVehicleType
  )
})
